import {Portal} from '@primer/react'
import {useWindowSize} from '@primer/react-brand'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef, useState} from 'react'

import type {ProductAnnouncementModalProps} from './../ProductAnnouncement/ProductAnnouncement.types'
import ProductAnnouncementModalDesktop from './ProductAnnouncementModalDesktop/ProductAnnouncementModalDesktop'
import ProductAnnouncementModalMobile from './ProductAnnouncementModalMobile/ProductAnnouncementModalMobile'

import Styles from './ProductAnnouncementModal.module.css'

export type {ProductAnnouncementModalProps} from './../ProductAnnouncement/ProductAnnouncement.types'

const defaultProps: Partial<ProductAnnouncementModalProps> = {
  shouldShowTabCtas: true,
  shouldShowMainCta: true,
}

const ProductAnnouncementModal = (props: ProductAnnouncementModalProps) => {
  const initializedProps: ProductAnnouncementModalProps = {...defaultProps, ...props}
  const {isOpen, title, onClose, onEvent, tabs, ...propsRest} = initializedProps

  const [activeTabIndex, setActiveTabIndex] = useState<number>(-1)
  const contentRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const isOpenRef = useRef<boolean>(isOpen)

  const {isLarge: isDesktopView} = useWindowSize()

  const onTabChange = useCallback(
    (tabIndex: number) => {
      const tabName = tabs[tabIndex]?.title
      if (tabName) {
        onEvent?.('tab_clicked', {tabName})
      }

      setActiveTabIndex(tabIndex)
    },
    [onEvent, tabs],
  )

  useEffect(() => {
    isOpenRef.current = isOpen

    if (isOpen) {
      // Store previously focused element
      previousFocusRef.current = document.activeElement as HTMLElement

      setActiveTabIndex(0)

      // Focus on the first tab
      const focusElement = contentRef.current?.querySelector<HTMLElement>('[tabindex]:not([tabindex="-1"]), [data-tab]')
      focusElement?.focus()
      if (!focusElement) contentRef.current?.focus()

      onEvent?.('opened')
    } else {
      setActiveTabIndex(-1)
      if (activeTabIndex >= 0) onEvent?.('closed')

      // Restore focus to previously focused element
      previousFocusRef.current?.focus()
      previousFocusRef.current = null
    }

    document.body.style.overflow = isOpen ? 'hidden' : ''
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isOpenRef.current) return

      if (event.key === 'Escape') {
        onClose()
        return
      }

      // Focus trap
      if (event.key === 'Tab') {
        const focusableSelector = 'button, [href], [tabindex]:not([tabindex="-1"])'
        const focusableElements = Array.from(
          contentRef.current?.querySelectorAll<HTMLElement>(focusableSelector) || [],
        ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null)

        if (!focusableElements.length) return

        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault()
            lastElement?.focus()
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault()
            firstElement?.focus()
          }
        }
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Portal>
      <div
        className={clsx(Styles['ProductAnnouncementModal'], isOpen && Styles['ProductAnnouncementModal--open'])}
        aria-label={title}
        aria-modal={isOpen}
        // @ts-expect-error `inert` exists
        inert={!isOpen ? 'true' : undefined}
        role="dialog"
        data-testid="product-announcement-modal"
      >
        <div role="none" className={Styles['ProductAnnouncementModal__backdrop']} onClick={onClose} />

        <div ref={contentRef} className={Styles['ProductAnnouncementModal__content']}>
          {isDesktopView && (
            <ProductAnnouncementModalDesktop
              currentTabIndex={activeTabIndex}
              onTabChange={onTabChange}
              title={title}
              tabs={tabs}
              onClose={onClose}
              onEvent={onEvent}
              {...propsRest}
            />
          )}

          {!isDesktopView && (
            <ProductAnnouncementModalMobile
              currentTabIndex={activeTabIndex}
              onTabChange={tabIndex =>
                onTabChange(tabIndex === activeTabIndex ? (tabIndex + 1) % tabs.length : tabIndex)
              }
              title={title}
              tabs={tabs}
              onClose={onClose}
              onEvent={onEvent}
              {...propsRest}
            />
          )}
        </div>
      </div>
    </Portal>
  )
}

export default ProductAnnouncementModal

try{ ProductAnnouncementModal.displayName ||= 'ProductAnnouncementModal' } catch {}